import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useConfig } from '../context/ConfigContext';
import { downloadListCsv } from '../utils/csv';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Receipts.jsx. Receipts are
// read-only/append-only from the UI (no edit/delete route on the backend —
// see routes/api.php, ReceiptController only exposes index/show/store/pdf).
interface ReceiptRow {
  id: number;
  receipt_number: string;
  customer_invoice: { invoice_number: string; customer_name: string } | null;
  payer_name: string;
  paid_amount: number;
  payment_method: string;
  payment_date: string;
}

export function Receipts() {
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') ?? '');

  const filters = useMemo(() => ({ search: search || undefined }), [search]);
  const { data, loading, error, setPage } = usePaginatedList<ReceiptRow>('/receipts', filters);

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<ReceiptRow>(
      api,
      '/receipts',
      { search: search || undefined },
      [
        { label: 'Receipt', value: (r) => r.receipt_number },
        { label: 'Type', value: (r) => (r.customer_invoice ? 'Credit Sale' : 'Cash Sale') },
        { label: 'Invoice', value: (r) => r.customer_invoice?.invoice_number ?? '' },
        { label: 'Customer', value: (r) => r.customer_invoice?.customer_name ?? r.payer_name },
        { label: 'Amount', value: (r) => r.paid_amount },
        { label: 'Method', value: (r) => r.payment_method },
        { label: 'Date', value: (r) => r.payment_date },
      ],
      'receipts.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Receipts</h1>
        <Link className="btn btn-primary" to="/revenue/receipts/new">
          + Add Receipt
        </Link>
      </header>
      <p className="muted">Payments received — either against a customer invoice, or as an immediate Cash Sale.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search receipt number or customer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Invoice</th>
                <th>Customer</th>
                <th className="right">Amount</th>
                <th>Method</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((r) => (
                  <tr key={r.id}>
                    <td className="muted">{r.receipt_number}</td>
                    <td className="muted">{r.customer_invoice?.invoice_number ?? 'Cash Sale'}</td>
                    <td>{r.customer_invoice?.customer_name ?? r.payer_name}</td>
                    <td className="right">{fmtMoney(r.paid_amount)}</td>
                    <td>{r.payment_method}</td>
                    <td>{r.payment_date}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/revenue/receipts/${r.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No receipts recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
